<template>
  <div class="company-parameters">
    <h3>{{ $t('company.your') }}</h3>
    <div v-if="isLoading" class="spinner-border text-info" role="status"></div>
    <div v-else>
      <div class="white-container-item row">
        <div class="white-container-item big">
          <div>{{ $t('company.name') }}</div>
          <input type="text" v-model="company.name" @change="company.name ? $emit('updateCompany', company) : null">
          <div v-if="company.name === ''" class="red-text">{{ gd  }}</div>
        </div>
        <div class="white-container-item big">
          <div>{{ $t('company.apeCode') }}</div>
          <div class="ape-autocomplete">
            <input
              type="text"
              v-model="apeQuery"
              @input="searchApeCodes"
              @focus="showApeSuggestions = apeSuggestions.length > 0"
              @blur="hideApeSuggestionsDelayed"
              :placeholder="company.ape || ''"
            >
            <div v-if="showApeSuggestions && apeSuggestions.length > 0" class="ape-suggestions">
              <div
                v-for="suggestion in apeSuggestions"
                :key="suggestion.id"
                class="ape-suggestion-item"
                @mousedown.prevent="selectApeCode(suggestion)"
              >
                <span class="ape-code">{{ suggestion.code }}</span>
                <span class="ape-text">{{ suggestion.text }}</span>
              </div>
            </div>
            <div v-if="isSearchingApe" class="ape-loading">
              <div class="spinner-border spinner-border-sm text-info" role="status"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="white-container-item" style="margin-top: 1.5rem">
        <div style="display: flex; gap: 0.5rem;">
          <div style="white-space: nowrap;">
            {{ $t('company.description') }}
          </div>
          <toolType
            :innerHtml="$t('company.descriptionText')"
          />
        </div>
        <textarea v-model="company.description" @keyup="$emit('updateCompany', company)"></textarea>
      </div>

      <!-- Logo upload section -->
      <div class="white-container-item" style="margin-top: 1.5rem">
        <div>{{ $t('company.logo') }}</div>
        <div v-if="isLoadingLogo" class="spinner-border text-info" role="status"></div>
        <div
          v-else
          class="logo-upload-zone"
          :class="{ 'has-logo': company.logo_name, 'drag-over': isDragging }"
          @dragover="dragover"
          @dragleave="dragleave"
          @drop="drop"
        >
          <label for="logo-upload" class="logo-upload-label">
            <div v-if="!company.logo_name" class="logo-upload-empty">
              <font-awesome-icon :icon="['fas', 'cloud-arrow-up']" size="2x" />
              <span>{{ $t('company.logoUpload') }}</span>
              <span class="logo-upload-hint">{{ $t('company.logoHint') }}</span>
            </div>
            <div v-else class="logo-preview-container">
              <img :src="logoUrl" alt="Logo" class="logo-preview-img" />
              <div class="logo-overlay">
                <font-awesome-icon :icon="['fas', 'pen']" />
                <span>{{ $t('company.logoChange') }}</span>
              </div>
            </div>
          </label>
          <input
            type="file"
            id="logo-upload"
            accept="image/*"
            @change="onChangeLogo"
            ref="logoFile"
          />
          <button
            v-if="company.logo_name"
            class="logo-delete-btn"
            @click.prevent="remove"
            :title="$t('general.remove')"
          >
            <font-awesome-icon :icon="['fas', 'trash']" />
          </button>
        </div>
      </div>
    </div>
  </div>


</template>
<script>
import instance from '../../main';
import toolType from './toolType.vue'

export default {
  components: {
    toolType
  },
  props: {
    propsCompany: Object
  },
  data() {
    return{
      isLoading: true,
      isLoadingLogo: true,
      company: this.propsCompany,
      logoUrl: null,
      newUploaderFile: null,
      isDragging: false,
      apeQuery: '',
      apeSuggestions: [],
      showApeSuggestions: false,
      isSearchingApe: false,
      apeSearchTimeout: null,
    }
  },
  async mounted() {
    this.isLoading = false
    if (this.company.ape) {
      this.apeQuery = this.company.ape;
    }
    if (this.company.logo_name) {
      await this.getLogo()
    }
    this.isLoadingLogo = false
  },
  computed: {

  },
  methods: {
    async searchApeCodes() {
      if (this.apeSearchTimeout) {
        clearTimeout(this.apeSearchTimeout);
      }

      // Si le champ est vidé, mettre à jour company.ape à vide et sauvegarder
      if (!this.apeQuery || this.apeQuery.trim() === '') {
        this.apeSuggestions = [];
        this.showApeSuggestions = false;
        if (this.company.ape) {
          this.company.ape = '';
          this.$emit('updateCompany', this.company);
        }
        return;
      }

      if (this.apeQuery.length < 2) {
        this.apeSuggestions = [];
        this.showApeSuggestions = false;
        return;
      }

      this.apeSearchTimeout = setTimeout(async () => {
        this.isSearchingApe = true;
        try {
          const lang = this.$i18n?.locale || 'fr';
          const response = await instance.get(`/ape-codes/search`, {
            headers: {
              "token": localStorage.getItem('token')
            },
            params: {
              q: this.apeQuery,
              lang: lang
            }
          });
          this.apeSuggestions = response.data.data || [];
          this.showApeSuggestions = this.apeSuggestions.length > 0;
        } catch (error) {
          console.log("Error while searching APE codes", error);
          this.apeSuggestions = [];
        } finally {
          this.isSearchingApe = false;
        }
      }, 300);
    },
    selectApeCode(suggestion) {
      this.company.ape = suggestion.code;
      this.apeQuery = suggestion.label;
      this.showApeSuggestions = false;
      this.apeSuggestions = [];
      this.$emit('updateCompany', this.company);
    },
    hideApeSuggestionsDelayed() {
      setTimeout(() => {
        this.showApeSuggestions = false;
      }, 200);
    },
    async getLogo() {
      await instance.get(`/companies/logo`, {
        headers : {
          "token": localStorage.getItem('token'),
          "company": localStorage.getItem('companyId')
        },
        responseType: 'blob'}).then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          this.logoUrl = url
      }).catch(function(error){
        console.log("Error while getting logo informations")
        console.log(error)
      })
    },
    onChangeLogo() {
      const newDocument = this.$refs.logoFile.files;
      if (!newDocument || !newDocument[0]) return;
      this.newUploaderFile = newDocument;
      this.company.logo_name = newDocument[0].name;
      this.logoUrl = URL.createObjectURL(newDocument[0]);
      this.updateLogo(this.newUploaderFile);
    },
    remove() {
      this.newUploaderFile = null;
      this.company.logo_name = null;
      this.logoUrl = null;
      this.updateLogo(null);
    },
    dragover(event) {
      event.preventDefault();
      this.isDragging = true;
    },
    dragleave(event) {
      event.preventDefault();
      this.isDragging = false;
    },
    drop(event) {
      event.preventDefault();
      this.isDragging = false;
      const droppedFiles = event.dataTransfer.files;
      if (!droppedFiles || !droppedFiles[0]) return;
      if (!droppedFiles[0].type.startsWith('image/')) return;
      this.newUploaderFile = droppedFiles;
      this.company.logo_name = droppedFiles[0].name;
      this.logoUrl = URL.createObjectURL(droppedFiles[0]);
      this.updateLogo(droppedFiles);
    },
    async updateLogo(newUploaderFile){
      this.file = newUploaderFile;
      let formData = new FormData();

      if (this.file) {
        // if (this.file[0].size > 15000000) {
        //   alert(this.$t('document.size')+"15MB");
        //   return;
        // }
        this.logoUrl = URL.createObjectURL(newUploaderFile[0]);
        this.file_name = newUploaderFile[0].name;
        this.company.logo_name = this.file_name;

        formData.append('file', this.file[0]); // Ajoute le fichier à l'objet FormData
      } else {
        this.file_name = null;
        formData.append('file', null);
      }

      instance.put(`/companies/logo`, formData, {
        headers: {
          "token": localStorage.getItem('token'),
          "company": localStorage.getItem('companyId'),
          'Content-Type': 'multipart/form-data' // Important pour indiquer que vous envoyez un fichier
        }
      }).then((response) => {
        console.log(response)
      }).catch(function(error){
        console.log("Error while updating a logo.");
        console.log(error)
      });
    },



  },
  created() {

  }
}

</script>
